import { AuditLogGridItemObject } from '@libs/types/src/object-history/object-type/audit-log.object';
import { commonTranslationFields, historyTranslationMap } from './audit-log-tree-v2.types';
import { TreeNode } from '@openng/optimus-ui/api';
import { translate } from '../../shared.global-service';

function getTranslationStringForHistoryItemKey(parentLabel: string, key: string) {
    if (commonTranslationFields.includes(key)) {
        return 'entities.history.commonFields.' + key;
    }

    return parentLabel in historyTranslationMap ? historyTranslationMap[parentLabel] + key : key;
}

function parseChanges(item: AuditLogGridItemObject, key: number) {
    const data = JSON.parse(item.data);
    const oldData = JSON.parse(item.oldData);
    const changedFields = JSON.parse(item.changedFields);

    const pushSimpleValues = (tree: TreeNode[], newValue: any, oldValue: any, key: string, parentLabel: string) => {
        tree.push({
            label: translate.instant(getTranslationStringForHistoryItemKey(parentLabel, key)),
            data: {
                newValue,
                oldValue,
            },
            children: [],
        });

        return newValue !== oldValue;
    };

    const parseArray = (tree: TreeNode[], newValue: any[], oldValue: any[], key: string, parentLabel: string) => {
        // TODO: does not work correctly when there is deletion from the middle of the array
        // TODO: we will probably need to add 'id' column to all entities to be able to match same entities in different arrays
        const subTree: TreeNode[] = [];
        const newValueLength = newValue?.length || 0;
        const oldValueLength = oldValue?.length || 0;
        let anyChange = false;

        for (let i = 0; i < Math.min(newValueLength, oldValueLength); i++) {
            // find changes
            const { children, anyChangeReturn, treeData } = parseChildren(newValue?.[i], oldValue?.[i], {}, key);
            if (anyChangeReturn && (children.length || treeData)) {
                subTree.push({
                    label: i.toString(),
                    data: treeData || 'fixLabelNumbering',
                    children,
                });
            }
            anyChange ||= anyChangeReturn;
        }
        if (newValueLength > oldValueLength) {
            // find additions
            for (let i = oldValueLength; i < newValueLength; i++) {
                const { children, anyChangeReturn, treeData } = parseChildren(newValue[i], {}, {}, key);
                if (anyChangeReturn && (children.length || treeData)) {
                    subTree.push({
                        label: i.toString(),
                        data: treeData || 'fixLabelNumbering',
                        children,
                    });
                }
                anyChange ||= anyChangeReturn;
            }
        } else if (newValueLength < oldValueLength) {
            // find deletions
            for (let i = newValueLength; i < oldValueLength; i++) {
                const { children, anyChangeReturn, treeData } = parseChildren({}, oldValue[i], {}, key);
                if (anyChangeReturn && (children.length || treeData)) {
                    subTree.push({
                        label: i.toString(),
                        data: treeData || 'fixLabelNumbering',
                        children,
                    });
                }
                anyChange ||= anyChangeReturn;
            }
        }
        if (subTree.length && anyChange) {
            tree.push({
                label: translate.instant(getTranslationStringForHistoryItemKey(parentLabel, key)),
                data: undefined,
                children: subTree,
            });
        }
        return anyChange;
    };

    const parseAttribute = (tree: TreeNode[], data: any, oldData: any, key: string, parentLabel: string) => {
        const newValue = data?.[key];
        const oldValue = oldData?.[key];

        if (newValue !== undefined && Array.isArray(newValue) || oldValue !== undefined && Array.isArray(oldValue)) {
            return parseArray(tree, newValue, oldValue, key, parentLabel);
        } else if (typeof newValue === 'object' && newValue !== null || typeof oldValue === 'object' && oldValue !== null) {
            const { children, anyChangeReturn, treeData } = parseChildren(newValue, oldValue, {}, key);
            if (anyChangeReturn && (children.length || treeData)) {
                tree.push({
                    label: translate.instant(getTranslationStringForHistoryItemKey(parentLabel, key)),
                    data: treeData,
                    children,
                });
            }
            return anyChangeReturn;
        } else {
            return pushSimpleValues(tree, newValue, oldValue, key, parentLabel);
        }
    };

    const parseChildren = (
        data: any,
        oldData: any,
        changedFields: any,
        parentLabel: string,
    ): { children: TreeNode[], anyChangeReturn: boolean, treeData: any } => {
        let anyChange = false;
        const tree: TreeNode[] = [];
        let treeData: any = undefined;

        if (Object.entries(changedFields).length) {
            Object.entries(changedFields).forEach(([key, value], index) => {
                if (typeof value === 'object' && value !== null) {
                    const {
                        children,
                        anyChangeReturn,
                        treeData,
                    } = parseChildren(data?.[key], oldData?.[key], value, key);
                    if (anyChangeReturn && (children.length || treeData)) {
                        tree.push({
                            label: translate.instant(getTranslationStringForHistoryItemKey(parentLabel, key)),
                            data: treeData,
                            children,
                        });
                    }
                    anyChange ||= anyChangeReturn;
                } else {
                    anyChange = parseAttribute(tree, data, oldData, key, parentLabel) || anyChange;
                }
            });

        } else if (Array.isArray(data)) {
            // array in array (probably won't happen)
            for (let i = 0; i < Math.min(data?.length || 0, oldData?.length || 0); i++) {
                const { children, anyChangeReturn, treeData } = parseChildren(data?.[i], oldData?.[i], {}, parentLabel);
                if (anyChangeReturn && (children.length || treeData)) {
                    tree.push({
                        label: i.toString(),
                        data: treeData || 'fixLabelNumbering',
                        children,
                    });
                }
                anyChange ||= anyChangeReturn;
            }

        } else if (typeof data != 'object' || typeof oldData != 'object') {
            let newValue = data;
            let oldValue = oldData;
            if (typeof data == 'object') {
                newValue = null;
            }
            if (typeof oldData == 'object') {
                oldValue = null;
            }

            anyChange = true;
            treeData = {
                newValue,
                oldValue,
            };

        } else if (data !== undefined && Object.keys(data).length) {
            Object.keys(data).forEach((key) => {
                anyChange = parseAttribute(tree, data, oldData, key, parentLabel) || anyChange;
            });

        } else if (oldData !== undefined && Object.keys(oldData).length) {
            Object.keys(oldData).forEach((key) => {
                anyChange = parseAttribute(tree, data, oldData, key, parentLabel) || anyChange;
            });
        }

        return { children: tree, anyChangeReturn: anyChange, treeData };
    };

    const finishTree = (nodes: TreeNode[], keyPrefix: string) => {
        nodes.forEach((node, index) => {
            node.key = keyPrefix + index;
            node.expanded = true;
            if (node.children?.length) {
                finishTree(node.children, `${keyPrefix}${index}-`);
            }
            if (node.data === 'fixLabelNumbering') {
                node.label = (++index).toString();
                node.data = undefined;
            }
        });
    };

    const { children, anyChangeReturn } = parseChildren(data, oldData, changedFields, item.entityName);
    finishTree(children, `${key}-`);
    return children;
}

function parseReads(item: AuditLogGridItemObject, key: number) {
    const data = JSON.parse(item.data);

    const parseChildren = (data: any, keyPrefix: string, parentLabel: string, _index?: number): TreeNode[] | any[] => {
        const tree: TreeNode[] = [];

        if (Array.isArray(data)) {
            data.forEach((value, index) => {
                const isObj = typeof value === 'object';
                tree.push({
                    key: keyPrefix + index,
                    label: (index + 1).toString(),
                    data: isObj ? undefined : value,
                    children: isObj ? parseChildren(value, `${keyPrefix}${index}-`, parentLabel, index + 1) : [],
                });
            });

        } else {
            Object.entries(data).forEach(([key, value], index) => {
                const nodeKey = keyPrefix + index.toString();
                const nodeLabel = translate.instant(getTranslationStringForHistoryItemKey(parentLabel, key));
                if (typeof value !== 'object' || value === null) {
                    tree.push({
                        key: nodeKey,
                        label: nodeLabel,
                        data: value ?? '-',
                        children: [],
                    });
                } else {
                    tree.push({
                        key: nodeKey,
                        label: nodeLabel,
                        data: undefined,
                        children: parseChildren(value, `${nodeKey}-`, key),
                    });
                }
            });
        }

        return tree;
    };

    return parseChildren(data, `${key}-`, item.entityName);
}

export function parseAuditLogHistoryV2(items: AuditLogGridItemObject[]) {
    const res: Record<number, TreeNode[]> = {};
    let key = 0;

    items.forEach((item: AuditLogGridItemObject) => {
        res[item.id] = item.action !== 'read' ? parseChanges(item, key++) : parseReads(item, key++);
    });

    return res;
}
